import type { TFunction } from 'i18next';
import type { Location, NavigateFunction } from 'react-router';

// Create a reusable tick renderer for PolarAngleAxis labels that adds
// an outward radial offset to improve spacing between the radar and labels.
export const createRadialLabelTickRenderer = (
    offsetPx: number,
    options?: { fontSize?: number; fill?: string; fontWeight?: number | string }
) => {
    const { fontSize = 12, fill = 'var(--color-neutral-500)', fontWeight = 500 } = options || {};
    const RadialLabelTickRenderer = (props: any) => {
        const { x, y, cx, cy, payload, textAnchor } = props;
        const dx = x - cx;
        const dy = y - cy;
        const distance = Math.sqrt(dx * dx + dy * dy) || 1;
        const nx = x + (dx / distance) * offsetPx;
        const ny = y + (dy / distance) * offsetPx;

        return (
            <text x={nx} y={ny} fill={fill} fontSize={fontSize} fontWeight={fontWeight} textAnchor={textAnchor}>
                {payload.value}
            </text>
        );
    };

    return RadialLabelTickRenderer;
};

/**
 * Calculate gradient color based on value (0-100)
 * Interpolates from emerald-400 rgb(110, 231, 183) to teal-400 rgb(45, 212, 191)
 */
export const getGradientColor = (val: number): string => {
    // Normalize value to 0-1
    const normalized = Math.max(0, Math.min(100, val)) / 100;

    // emerald-400: rgb(110, 231, 183)
    // teal-400: rgb(45, 212, 191)
    const startColor = [110, 231, 183];
    const endColor = [45, 212, 191];

    // Interpolate RGB values
    const r = Math.round(startColor[0] + (endColor[0] - startColor[0]) * normalized);
    const g = Math.round(startColor[1] + (endColor[1] - startColor[1]) * normalized);
    const b = Math.round(startColor[2] + (endColor[2] - startColor[2]) * normalized);

    return `rgb(${r}, ${g}, ${b})`;
};

/**
 * Prefer restoring an exact previous page (including any UI-key / session state),
 * otherwise fall back to a provided URL.
 */
export const backToModule = (args: { navigate: NavigateFunction; location: Location; fallback: string }) => {
    const from = (args.location.state as { from?: string } | undefined)?.from;

    if (from) {
        args.navigate(from);
        return;
    }

    args.navigate(args.fallback);
};

/** @deprecated Use `backToModule` instead */
export const backToAssessments = backToModule;

/**
 * Generate navigation options for question selector dropdown
 */
export const getQuestionNavigationOptions = (
    totalQuestions: number,
    totalAnswered: number,
    t: TFunction
): { value: string; label: string; disabled: boolean }[] => {
    return Array.from({ length: totalQuestions }, (_, index) => ({
        value: String(index),
        label: t('question.questionCounter', {
            current: index + 1,
            total: totalQuestions,
        }),
        disabled: index >= totalAnswered,
    }));
};

/**
 * Format milliseconds to MM:SS format
 * @param ms - Time in milliseconds
 * @returns Formatted string in MM:SS format
 */
export const formatTimeMMSS = (ms: number): string => {
    const totalSeconds = Math.ceil(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};
